/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable no-nested-ternary */
import cn from 'classnames';
import React, {
  memo, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import {
  requestMobileSearch,
  requestVacancies,
} from '../../redux/actions/main-page';
import { ICatalogReducerState } from '../../redux/reducers/catalogReducer';
import { RootState } from '../../redux/reducers/rootReducer';
import { clearFromHTML } from '../../utils/clear-html';
import { CATEGORIES_WITH_NAME_AS_KEY } from '../../utils/constants/categories-with-name-as-key';
import { IFilterSelectsUI } from '../../utils/constants/filter-selects';
import mediaQueries from '../../utils/media-query/media-query-constants';
import useMediaQuery from '../../utils/media-query/media-query-hook';
import { IVacancy } from '../../utils/types/state-types';
import { useChangeFilters } from '../../utils/useChangeFilters';
import FiltersIcon from '../SVGIcons/FiltersIcon';
import SearchIcon from '../SVGIcons/SearchIcon';
import { Button } from '../UI/Button';
import { Modal } from '../UI/Modal';
import Search from '../UI/Search';
import { VacanciesFilter } from '../VacanciesCatalog/VacanciesFilter';
import classes from './search-vacancies.module.scss';

interface SearchVacanciesProps {
  isCatalog?: boolean;
}

function SearchVacancies({ isCatalog }: SearchVacanciesProps) {
  const vacancies = useSelector<RootState, IVacancy[]>(
    (state) => state.mainReducer.vacancies ?? [],
  );

  const isLoading = useSelector<RootState, boolean>(
    (state) => state.mainReducer.isLoading,
  );

  const { search } = useLocation();

  const res = useSelector<RootState, ICatalogReducerState['filters'] | []>(
    (state) => state.catalogReducer.filters,
  );

  const [filters, setFilters] = useState<null | IFilterSelectsUI[]>();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (res.length > 0) {
      // Получаем параметры из фильтров и строки запроса
      const getParams = res?.map((filter) => ({
        category: filter.category,
        data: searchParams
          .getAll(filter.category)
          .flatMap((data) => data.split(',')),
      }));

      const newFilter = res?.map((item) => {
        const currentParams = getParams?.find(
          (filter) => filter.category === item.category,
        );
        const isNameAsKey = CATEGORIES_WITH_NAME_AS_KEY.includes(item.category);

        // Обновляем данные для каждого фильтра
        const updatedData = item.data.map((itemData) => ({
          ...itemData,
          isSelected:
            currentParams?.data.includes(
              isNameAsKey ? itemData.name : itemData.id,
            ) || false,
        }));

        return {
          ...item,
          data: updatedData,
        };
      });

      setFilters(newFilter as IFilterSelectsUI[]);
    }
  }, [res, search]);

  const [isSearching, setIsSearching] = useState(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { clearAllFilterCategory } = useChangeFilters({
    filters,
  });

  const dispatch = useDispatch();

  const clearCategory = () => {
    setFilters(clearAllFilterCategory());
  };

  const getSearchValue = useMemo(() => {
    const urlParams = new URLSearchParams(search.split('?')[1]); // Извлекаем параметры из URL
    return urlParams.get('search');
  }, [search]);

  const [searchValue, setSearchValue] = useState<string>(getSearchValue || '');

  const isMobile = useMediaQuery(mediaQueries.mobile);

  useEffect(
    () => () => {
      dispatch(requestMobileSearch({ isSearchOpen: false }));
    },
    [],
  );

  const searchVacancies = (value: string) => {
    const params = searchParams;
    setSearchValue(value);

    if (value) {
      setIsSearching(true);
      if (!isCatalog) {
        params.set('search', value.trim());
      }
      dispatch(requestVacancies({ searchValue: value.trim() }));
    } else {
      setIsSearching(false);
      if (!isCatalog) {
        params.delete('search');
      }
      dispatch(requestVacancies({ searchValue: '' }));
    }

    setSearchParams(params);
  };

  const onCloseModal = () => {
    setModalOpen(false);
    dispatch(requestMobileSearch({ isSearchOpen: false }));
  };

  const isShowClear = useMemo(
    () => filters?.some((itemFilter) => itemFilter.data.some((itemData) => itemData.isSelected)),
    [filters],
  );

  const onEnterSubmit = (value: string) => {
    const params = searchParams;

    if (value) {
      params.set('search', value.trim());
    } else {
      params.delete('search');
    }

    if (isMobile) {
      onCloseModal();
    }

    setIsSearching(false);
    setSearchParams(params);
  };

  const onBlur = () => {
    const params = searchParams;

    if (searchValue) {
      params.set('search', searchValue.trim());
    } else {
      params.delete('search');
    }

    setSearchParams(params);
  };

  const onFilterClick = () => {
    dispatch(requestMobileSearch({ isSearchOpen: true }));
    setModalOpen(true);
  };

  const onClearClick = () => {
    const params = searchParams;
    params.delete('search');
    setSearchParams(params);
  };

  const renderIcon = () => <SearchIcon />;

  function getHighlightedText(text: string, higlight: string) {
    const parts = text.split(new RegExp(`(${higlight})`, 'gi'));
    return parts.map((part) => (
      <React.Fragment key={part + Math.random() * 100000}>
        {part.toLowerCase() === higlight.toLowerCase() ? <b>{part}</b> : part}
      </React.Fragment>
    ));
  }

  return (
    <div className={cn(classes.container)}>
      <div
        tabIndex={0}
        className={cn({
          [classes.searchContainer]: true,
          [classes.hiddenSearch]: !isSearching,
        })}
      >
        <div className={classes.searchContainerInner}>
          {isLoading ? (
            <p className={classes.helperText}>Поиск вакансий</p>
          ) : (
            <>
              {vacancies.length > 0 && searchValue && (
                <div className={classes.vacanciesContainer}>
                  {vacancies.map((vacancy) => (
                    <Link
                      key={vacancy.id}
                      to={`/vacancies/id/${vacancy.idForUrl}`}
                    >
                      <p>
                        {getHighlightedText(
                          clearFromHTML(vacancy.title),
                          search?.split('=')?.at(-1) || '',
                        )}
                      </p>
                    </Link>
                  ))}
                </div>
              )}
              {vacancies.length === 0 && search && (
                <p className={classes.helperText}>Вакансий не найдено</p>
              )}
            </>
          )}
        </div>
      </div>

      <div className={classes.search}>
        <Search
          icon={renderIcon}
          title="Найди свою вакансию"
          onSearch={searchVacancies}
          onEnterSubmit={onEnterSubmit}
          initialValue={searchValue ? decodeURI(searchValue) : ''}
          setIsSearching={setIsSearching}
          onBlur={onBlur}
          onClearClick={onClearClick}
          onClickBtn={() => {
            setIsSearching(false);
          }}
          isSearchVacancies={!isCatalog}
        />

        {isCatalog && isMobile && (
          <Button
            type="button"
            variant="secondary"
            className={cn(classes['search-filters'])}
            onClick={onFilterClick}
          >
            <FiltersIcon fill="black" />
          </Button>
        )}
      </div>

      {isCatalog && isMobile && (
        <Modal
          isOpen={modalOpen}
          onClose={onCloseModal}
          title="Фильтры"
          isShowHeader
          onClear={clearCategory}
          isShowClear={isShowClear}
          bodyClassName={classes['modal-filters-body']}
          className={classes['modal-filters']}
          fullHeight
          bottomButton={{
            text: 'Применить',
            onClick: onCloseModal,
          }}
        >
          <VacanciesFilter />
        </Modal>
      )}
    </div>
  );
}

export default memo(SearchVacancies);
